import { useState } from 'react'
import { AlertCircle, Loader2, Plus, Tag } from 'lucide-react'
import { useCategories, useDeleteCategory } from '@/hooks/useCategories'
import type { Category } from '@/lib/supabase'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
} from '@atrvd/saas-ui'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { CategoriesTable } from '@/components/banking/CategoriesTable'
import { CategoryFormDialog } from '@/components/banking/CategoryFormDialog'

/**
 * DCP-321: the Categories list is paginated 20 rows per page. The pagination UI is
 * rendered only when totalPages > 1, so a tenant with 20 or fewer categories gets a
 * single un-paginated list — "paginate only after 20+ items".
 */
const CATEGORIES_PAGE_SIZE = 20

/**
 * Settings page for managing transaction categories
 * Allows users to create, edit, and delete categories for their organization
 */
export function SettingsCategoriesPage() {
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  // Type filter removed — categories no longer have a user-facing type concept
  const { data: categoriesData, isLoading, isError, error, refetch } = useCategories(currentPage, CATEGORIES_PAGE_SIZE)
  const deleteCategory = useDeleteCategory()

  // Extract categories array and totals from paginated response
  const categories = categoriesData?.items ?? []
  const totalCategories = categoriesData?.total ?? 0
  const totalPages = Math.ceil(totalCategories / CATEGORIES_PAGE_SIZE)

  // DCP-321: pagination is server-side, so a page past the end returns an empty range
  // while `total` (a separate exact-count query, unaffected by .range()) stays correct —
  // the table would render "No categories yet" next to a "Categories (20)" header with
  // the pagination controls already hidden and no way back (repro: delete the only row
  // of the last page, or switch org while on page 2+). Adjusted during render rather
  // than in an effect: React discards this render and re-renders before painting, so the
  // empty table never flashes. Guarded on totalPages > 0 so a genuinely empty list
  // (and the loading render, where total is 0) never clamps to page 0.
  if (totalPages > 0 && currentPage > totalPages) {
    setCurrentPage(totalPages)
  }

  /**
   * Handle creating a new category
   */
  const handleCreate = () => {
    setEditingCategory(null)
    setIsFormDialogOpen(true)
  }

  /**
   * Handle editing an existing category
   */
  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setIsFormDialogOpen(true)
  }

  /**
   * Handle opening delete confirmation dialog
   */
  const handleDeleteClick = (category: Category) => {
    setDeletingCategory(category)
  }

  /**
   * Handle confirming category deletion
   */
  const handleDeleteConfirm = async () => {
    if (!deletingCategory) return

    try {
      await deleteCategory.mutateAsync(deletingCategory.id)
      setDeletingCategory(null)
    } catch (error) {
      // Error is handled by the mutation's onError callback
      // Close dialog on error too
      setDeletingCategory(null)
    }
  }

  /**
   * Handle closing the form dialog
   */
  const handleFormDialogClose = () => {
    setIsFormDialogOpen(false)
    setEditingCategory(null)
  }

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h3 className="text-lg font-semibold">Failed to load categories</h3>
          <p className="text-sm text-muted-foreground">{error?.message || 'Please try again later'}</p>
        </div>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Categories ${totalCategories > 0 ? `(${totalCategories})` : ''}`}
        description="Manage transaction categories for your organization"
        actions={
          <Button
            onClick={handleCreate}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Category
          </Button>
        }
      />

      {/* Categories Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Categories
            {totalCategories > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({totalCategories})
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Organize transactions by assigning them to categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CategoriesTable
            categories={categories}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            // DCP-321: no pagination prop while everything fits on one page — the
            // controls (and the "Showing X-Y of Z" footer, which DataTable renders as
            // one unit with them) appear only from the 21st category.
            pagination={totalPages > 1 ? {
              currentPage,
              totalPages,
              pageSize: CATEGORIES_PAGE_SIZE,
              totalItems: totalCategories,
              onPageChange: setCurrentPage,
            } : undefined}
          />
        </CardContent>
      </Card>

      {/* Category Form Dialog */}
      <CategoryFormDialog
        open={isFormDialogOpen}
        onOpenChange={handleFormDialogClose}
        category={editingCategory}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!deletingCategory}
        onOpenChange={(open: boolean) => !open && !deleteCategory.isPending && setDeletingCategory(null)}
        title="Delete Category"
        description={`Are you sure you want to delete "${deletingCategory?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        isLoading={deleteCategory.isPending}
        variant="destructive"
      />
    </div>
  )
}
